import React, { useState } from 'react';
import API from '../../services';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setBrowser, setIsLoggedIn } from '../../store/authReducer';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
    userData: {
        mobileNumber: string;
        password: string;
        countryCode: string;
    };
    updateUserData: (data: any) => void;
    goToNextStep: () => void;
    startOtpTimer: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
    userData,
    updateUserData,
    goToNextStep,
    startOtpTimer
}) => {
    const dispatch = useDispatch();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [isExistingUser, setIsExistingUser] = useState(false);

    const validateMobileNumber = (value: string) => {
        if (!/^\d{10}$/.test(value)) {
            return 'Please enter a valid 10-digit mobile number';
        }
        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'mobileNumber' && value.length > 10) return;

        updateUserData({ [name]: value });

        // Clear existing error
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Validate mobile number
        if (name === 'mobileNumber') {
            const error = validateMobileNumber(value);
            if (error) {
                setErrors(prev => ({ ...prev, mobileNumber: error }));
            }
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate mobile number
        const mobileError = validateMobileNumber(userData.mobileNumber);
        if (mobileError) {
            setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
            return;
        }

        if (!userData.password) {
            setErrors(prev => ({ ...prev, password: 'Password is required' }));
            return;
        }

        setLoading(true);

        try {

            const response = await API.post(`/auth/login`, {
                groupBotId: "1844",
                mobileNumber: userData.mobileNumber,
                countryCode: userData.countryCode,
                password: userData.password,
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
            } else {
                if (data.status === "RS_OK" && data.userStatus === "BLOCKED") {
                    sessionStorage.setItem("user_blocked", "true");
                }
                sessionStorage.setItem("userKey", response?.data?.userKey)
                sessionStorage.setItem("token", response?.data?.accessToken);
                sessionStorage.setItem("rtoken", response?.data?.refreshToken);
                dispatch(setIsLoggedIn(true));
                dispatch(setBrowser("Web"));
                toast.success("Logged In Succesfully");
                navigate("/home")
            }
        } catch (error) {
            console.error("Login failed:", error);
            setErrors(prev => ({ ...prev, form: 'An error occurred during login' }));
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        const mobileError = validateMobileNumber(userData.mobileNumber);
        if (mobileError) {
            setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
            return;
        }

        setOtpLoading(true);

        try {
            const response = await API.post(`/auth/send-otp`, {
                mobileNumber: userData.mobileNumber,
                countryCode: "91",
                groupBotId: "1844"
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
                setOtpLoading(false);
                setErrors(prev => ({ ...prev, form: data.errorDescription || 'Failed to send OTP' }));
            } else {
                toast.success("OTP Sent Succesfully");
                updateUserData({ otpToken: response.data.token });
                startOtpTimer();
                goToNextStep();
            }
        } catch (error) {
            console.error("Failed to send OTP:", error);
            setErrors(prev => ({ ...prev, form: 'An error occurred while sending OTP' }));
        } finally {
            setOtpLoading(false);
        }
    };

    const handleCheckUser = async (e: React.FormEvent) => {
        e.preventDefault();
        const mobileError = validateMobileNumber(userData.mobileNumber);
        if (mobileError) {
            setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
            return;
        }
        setLoading(true);
        try {
            // Replace with your actual API endpoint for checking user existence
            const response = await API.post('/auth/is-existing-user', {
                mobileNumber: userData.mobileNumber,
                countryCode: userData.countryCode,
            });
            const data = await response.data;
            if (data.status !== 'RS_OK') {
                toast.error(data.errorDescription || 'Failed to check user');
                setErrors(prev => ({ ...prev, form: data.errorDescription || 'Failed to check user' }));
            } else {

                if (data.isExistingUser && data.isPasswordSet) {
                    setIsExistingUser(true);
                } else {
                    updateUserData({ otpToken: data.token });
                    startOtpTimer();
                    goToNextStep();

                }
            }
        } catch (error) {
            console.error('User check failed:', error);
            setErrors(prev => ({ ...prev, form: 'An error occurred while checking user' }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {
                !isExistingUser && (
                    <form className="flex flex-col w-full" onSubmit={handleCheckUser}>
                        <label className="text-sm text-zinc-400 mb-1">
                            Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <span className="px-3 py-2 text-sm bg-zinc-700 text-white border border-r-0 border-zinc-700 rounded-l-md">
                                +{userData.countryCode}
                            </span>
                            <input
                                type="number"
                                name="mobileNumber"
                                placeholder="Mobile number"
                                value={userData.mobileNumber}
                                onChange={handleChange}
                                className={`p-2 w-full border rounded-r-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mobileNumber ? 'border-red-500' : ''
                                    }`}
                            />
                        </div>
                        {errors.mobileNumber && (
                            <span className="text-red-500 text-xs mt-1">{errors.mobileNumber}</span>
                        )}


                        <button
                            type="submit"
                            className="p-2.5 mt-4 w-full text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                            disabled={loading || userData.mobileNumber.length !== 10}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Checking...
                                </>
                            ) : (
                                'Login'
                            )}
                        </button>
                    </form>
                )
            }
            {
                isExistingUser && (
                    <form className="flex flex-col w-full" onSubmit={handleLogin}>
                        <div className="flex items-center justify-between mb-4">
                            <label className="text-sm text-zinc-400">
                                Mobile Number
                            </label>
                            <div className="flex items-center">
                                <span className="text-sm text-white">+{userData.countryCode} {userData.mobileNumber}</span>
                                <button
                                    type="button"
                                    onClick={() => setIsExistingUser(false)}
                                    className="ml-2 text-blue-400 text-sm hover:underline"
                                >
                                    Edit
                                </button>
                            </div>
                        </div>

                        <label className="text-sm text-zinc-400  mb-1">
                            Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative w-full">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="Password"
                                value={userData.password}
                                onChange={handleChange}
                                className={`p-2 pr-10 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : ''
                                    }`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((prev) => !prev)}
                                className="absolute inset-y-0 right-2 flex items-center text-white"
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <span className="text-red-500 text-xs mt-1">{errors.password}</span>
                        )}

                        <div className="text-right mt-2 mb-4">
                            <button
                                type="button"
                                onClick={handleSendOtp}
                                className={`text-sm ${userData.mobileNumber.length === 10
                                    ? 'text-blue-400 hover:underline'
                                    : 'text-zinc-500 cursor-not-allowed'
                                    }`}
                                disabled={userData.mobileNumber.length !== 10 || otpLoading}
                            >
                                {otpLoading ? 'Sending OTP...' : 'Continue with OTP'}
                            </button>
                        </div>

                        {errors.form && (
                            <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
                                <p className="text-red-200 text-xs">{errors.form}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="p-2.5 w-full text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                            disabled={loading || userData.mobileNumber.length !== 10 || !userData.password}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Logging in...
                                </>
                            ) : (
                                'LOGIN'
                            )}
                        </button>
                    </form>
                )
            }
        </>
    );
};

export default LoginForm;